import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const isDev = process.env.NODE_ENV === 'development';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

// Helper function to check if token is expired
const isTokenExpired = (token) => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp * 1000 < Date.now();
    } catch (error) {
        return true;
    }
};

// Helper function to parse JWT token
const parseJwt = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (error) {
        return null;
    }
};

export function AuthProvider({ children }) {
    isDev && console.log('AuthProvider rendering');
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tokenVersion, setTokenVersion] = useState(0);
    const [isDevelopment] = useState(process.env.NODE_ENV === 'development');

    const refreshToken = useCallback(async () => {
        try {
            const refresh = localStorage.getItem('refresh');
            if (!refresh) {
                throw new Error('No refresh token');
            }

            const response = await axios.post('/api/token/refresh/', {
                refresh: refresh
            });

            // Store both new tokens if provided
            if (response.data.access) {
                localStorage.setItem('access', response.data.access);
            }
            if (response.data.refresh) {
                localStorage.setItem('refresh', response.data.refresh);
            }

            // Update user state from new access token
            const payload = parseJwt(response.data.access);
            if (payload && payload.username) {
                setUser({ username: payload.username });
            }

            setTokenVersion(prev => prev + 1);
            return true;
        } catch (error) {
            console.error('Token refresh failed:', error);
            if (error.response && error.response.status === 401) {
                // Clear tokens and user state only on 401 (Unauthorized)
                localStorage.removeItem('access');
                localStorage.removeItem('refresh');
                setUser(null);
            }
            return false;
        }
    }, []);

    // Parse token on mount and whenever tokenVersion changes
    useEffect(() => {
        const initializeAuth = async () => {
            isDev && console.log('Checking token status...');
            const access = localStorage.getItem('access');
            const refresh = localStorage.getItem('refresh');

            if (!access || !refresh) {
                isDev && console.log('No tokens found');
                setUser(null);
                setLoading(false);
                return;
            }

            if (!isTokenExpired(access)) {
                isDev && console.log('Valid access token found');
                const payload = parseJwt(access);
                if (payload && payload.username) {
                    isDev && console.log('Setting user from access token:', payload.username);
                    setUser({ username: payload.username });
                } else {
                    isDev && console.log('No username in token payload');
                    setUser(null);
                }
            } else if (!isTokenExpired(refresh)) {
                isDev && console.log('Access token expired, attempting refresh');
                const success = await refreshToken();
                if (!success) {
                    isDev && console.log('Token refresh failed');
                    setUser(null);
                }
            } else {
                isDev && console.log('Both tokens expired');
                localStorage.removeItem('access');
                localStorage.removeItem('refresh');
                setUser(null);
            }
            setLoading(false);
        };

        initializeAuth();
    }, [tokenVersion, refreshToken]);

    const login = async (username, password) => {
        isDev && console.log('Attempting login...');
        try {
            const res = await axios.post('/api/token/', { username, password }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            isDev && console.log('Login response:', res.data);

            if (!res.data.access || !res.data.refresh) {
                console.error('Missing tokens in response');
                throw new Error('Invalid server response');
            }

            localStorage.setItem('access', res.data.access);
            localStorage.setItem('refresh', res.data.refresh);

            const payload = parseJwt(res.data.access);
            isDev && console.log('Token payload:', payload);

            if (payload && payload.username) {
                isDev && console.log('Login successful:', payload.username);
                setUser({ username: payload.username });
            } else {
                console.error('No username in token payload after login');
                throw new Error('Invalid token payload');
            }
        } catch (error) {
            console.error('Login error:', error.response?.data || error.message);
            localStorage.removeItem('access');
            localStorage.removeItem('refresh');
            setUser(null);
            throw error;
        }
    };

    const logout = () => {
        isDev && console.log('Logging out...');
        localStorage.removeItem('access');
        localStorage.removeItem('refresh');
        setUser(null);
    };

    const register = async (username, password) => {
        isDev && console.log('Attempting registration...');
        await axios.post('/register/', { username, password });
        isDev && console.log('Registration successful');
    };

    const isAuthenticated = !!user;

    const value = {
        user,
        setUser,
        isAuthenticated,
        login,
        logout,
        register,
        refreshToken,
        loading,
        isDevelopment
    };

    isDev && console.log('AuthProvider current state:', {
        user,
        loading,
        isDevelopment
    });

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
} 